import { useEffect, useRef } from "react";

import { round } from "lodash";

import type { MultiLegOrderSchema } from "../schema";
import type { CombinedLeg } from "../utils";

export const areLegsEqual = (a: CombinedLeg[], b: CombinedLeg[]) => {
  if (a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i++) {
    const areEqual =
      a[i].form.symbol === b[i].form.symbol &&
      a[i].form.ratioQty === b[i].form.ratioQty &&
      a[i].form.positionIntent === b[i].form.positionIntent &&
      a[i].data.quoteSide === b[i].data.quoteSide;

    if (!areEqual) {
      return false;
    }
  }
  return true;
};

export const getLimitPrice = (legs: CombinedLeg[]) =>
  legs.reduce(
    (acc, { form: { positionIntent, ratioQty }, data: { contract } }) => {
      const mid =
        (((contract.snapshot?.latestQuote?.bp ?? 0) +
          (contract.snapshot?.latestQuote?.ap ?? 0)) /
          2) *
        (positionIntent.includes("sell") ? -1 : 1) *
        (Number.isNaN(ratioQty) ? 0 : ratioQty);
      return acc + mid;
    },
    0,
  );

interface UseLimitPriceParams {
  legs: CombinedLeg[];
  orderType: MultiLegOrderSchema["orderType"];
  setLimitPrice: (value: number) => void;
}

export const useLimitPrice = ({
  legs,
  orderType,
  setLimitPrice,
}: UseLimitPriceParams) => {
  const staleLegsRef = useRef(legs);

  useEffect(() => {
    const shouldUpdate =
      orderType === "market" || !areLegsEqual(staleLegsRef.current, legs);

    if (shouldUpdate) {
      const limitPrice = getLimitPrice(legs);
      setLimitPrice(round(limitPrice, 2));
      staleLegsRef.current = legs;
    }
  }, [legs, orderType, setLimitPrice]);
};
